// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../vendor/logo.png'; // Ensure the path is correct

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isMobile, setIsMobile] = useState(false); // State to detect mobile devices
  const navigate = useNavigate();

  // State for gradient position
  const [gradientPosition, setGradientPosition] = useState({ top: '0px', left: '0px' });

  // Function to generate random positions within the viewport
  const generateRandomPosition = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Gradient size (width and height)
    const gradientSize = 1152; // Triple the size from 384px to 1152px

    // Ensure the gradient stays partially within the viewport
    const maxLeft = viewportWidth - gradientSize;
    const maxTop = viewportHeight - gradientSize;

    const randomLeft = Math.random() * maxLeft;
    const randomTop = Math.random() * maxTop;

    return { top: `${randomTop}px`, left: `${randomLeft}px` };
  };

  useEffect(() => {
    // Function to check if the device is mobile
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // You can adjust the breakpoint as needed
    };

    // Initial check
    checkIsMobile();

    // Event listener for window resize
    window.addEventListener('resize', checkIsMobile);

    // Set initial random position
    setGradientPosition(generateRandomPosition());

    // Function to update gradient position
    const updatePosition = () => {
      setGradientPosition(generateRandomPosition());
    };

    // Update position every 15 seconds
    const intervalId = setInterval(updatePosition, 15000); // 15000ms = 15s

    // Cleanup
    return () => {
      clearInterval(intervalId); // Cleanup interval on unmount
      window.removeEventListener('resize', checkIsMobile); // Cleanup resize listener
    };
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();

    try {
      const response = await fetch(
        'https://backend.hybridprotect.hybridhosting.co/api/v1/auth/login',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        }
      );

      if (!response.ok) {
        const { message } = await response.json();
        setErrorMessage(message || 'Login failed');
        return;
      }

      // The server returns { token, fullname } on success
      const data = await response.json();
      const token = data.token;
      const fullname = data.fullname; // e.g. "John Doe"

      // Build a user object for React state
      const userObject = {
        username,
        token,
        fullname,
      };

      onLogin(userObject);
      navigate('/');
    } catch (error) {
      setErrorMessage('An error occurred during login');
      console.error(error);
    }
  };

  return (
    <div className="relative flex flex-col items-center justify-center h-screen bg-[#172031] overflow-hidden">
      {/* Gradient Background */}
      <div
        className="absolute transition-all duration-[15000ms] ease-in-out"
        style={{
          width: '1152px', // Triple the size from 384px to 1152px
          height: '1152px',
          top: gradientPosition.top,
          left: gradientPosition.left,
          background: 'radial-gradient(circle, rgba(14, 165, 233, 0.1) 0%, rgba(14, 165, 233, 0) 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
        }}
      ></div>

      {/* Conditional Rendering: Login Form or Mobile Message */}
      {!isMobile ? (
        // Login Card
        <div className="relative p-6 max-w-sm w-full bg-dark-card border border-gray-700 rounded-lg shadow-md z-10">
          {/* Logo */}
          <img src={logo} alt="Logo" className="mx-auto mb-4 h-12" />

          <h2 className="mb-6 text-2xl font-bold text-center text-white">Login</h2>

          <form onSubmit={handleLogin}>
            {errorMessage && (
              <div className="text-red-500 text-center mb-4">{errorMessage}</div>
            )}

            <input
              className="w-full p-3 mb-3 rounded bg-[#272E3B] border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />

            <input
              className="w-full p-3 mb-4 rounded bg-[#272E3B] border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <button
              type="submit"
              className="w-full p-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Login
            </button>
          </form>
        </div>
      ) : (
        // Mobile Message
        <div className="relative p-6 max-w-sm w-full bg-dark-card border border-gray-700 rounded-lg shadow-md z-10 flex flex-col items-center justify-center">
          {/* Logo (Optional) */}
          <img src={logo} alt="Logo" className="mx-auto mb-4 h-12" />

          <h2 className="mb-4 text-2xl font-bold text-center text-white">Hybrid Protect Panel</h2>
          <p className="text-center text-white">
            Hybrid Protect Panel is still in beta, mobile use is currently not ready for public use.
            Expected in Version v0.2.0.
          </p>
        </div>
      )}

      {/* Version Label */}
      <div className="relative group mt-4">
        <div className="text-white text-xs bg-[#262F3D] rounded-full px-2 py-1 text-center cursor-pointer">
          v0.1.1
        </div>
        <div className="absolute top-full left-1/4 transform -translate-x-1/4 mt-2 bg-gray-700 text-white text-xs rounded px-4 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-64 text-center pointer-events-none group-hover:pointer-events-auto">
          This is version v0.1.1 of the Hybrid Protect Panel. If you would like
          to learn more, visit our{' '}
          <a
            href="https://hybridhosting.co/blog/info/hybrid-protect-release-notes-v0.1.0"
            className="text-blue-400 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            release notes
          </a>
          .
        </div>
      </div>
    </div>
  );
}

export default Login;